/* Theme custom file */
import { Component } from '@theme/component';

/**
 * Mini-search dropdown component.
 *
 * Manages open/close state, backdrop blur overlay, scroll lock,
 * and transparent header override. Designed to wrap
 * <predictive-search-component> for search functionality.
 */
export class MiniSearch extends Component {
  requiredRefs = ['panel', 'backdrop'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {boolean} */
  #isOpen = false;

  /** @type {number} */
  #savedScrollY = 0;

  /** @type {string|null} */
  #savedTransparent = null;

  /** @type {string} */
  #defaultResultsHTML = '';

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    this.refs.backdrop?.addEventListener('click', () => this.close(), { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    this.#searchInput?.addEventListener('input', this.#onInputChange, { signal });
    this.#bindTrendingPills();

    const resultsContainer = this.querySelector('[ref="predictiveSearchResults"]');
    if (resultsContainer) {
      this.#defaultResultsHTML = resultsContainer.innerHTML;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get isOpen() {
    return this.#isOpen;
  }

  toggle() {
    if (this.#isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.#isOpen) return;
    this.#isOpen = true;
    this.setAttribute('open', '');
    this.#lockScroll();
    this.#overrideTransparentHeader(true);
    this.#focusInput();
  }

  close() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.removeAttribute('open');
    this.#resetSearch();
    this.#unlockScroll();
    this.#overrideTransparentHeader(false);
    this.#forceHeaderVisible();
  }

  get #searchInput() {
    return /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="searchInput"]'));
  }

  #focusInput() {
    requestAnimationFrame(() => this.#searchInput?.focus());
  }

  #resetSearch() {
    const input = this.#searchInput;
    if (input) input.value = '';
    this.#restoreDefaultResults();
  }

  #restoreDefaultResults() {
    const container = this.querySelector('[ref="predictiveSearchResults"]');
    if (container && this.#defaultResultsHTML) {
      container.innerHTML = this.#defaultResultsHTML;
    }
  }

  /** @param {Event} event */
  #onInputChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.value.trim().length) {
      this.#restoreDefaultResults();
    }
  };

  #bindTrendingPills() {
    const { signal } = this.#controller;

    for (const pill of this.querySelectorAll('.mini-search__trending-pill')) {
      pill.addEventListener('click', () => {
        const input = this.#searchInput;
        if (!input) return;
        input.value = pill.textContent.trim();
        input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
      }, { signal });
    }
  }

  /**
   * After closing, force the header back into its visible sticky state.
   *
   * Scroll nudging does not work here because Horizon's scroll handler
   * has a guard (`if (!this.#offscreen && stickyMode !== 'always') return`)
   * that blocks processing when the sticky header is still intersecting.
   * Instead, we wait for the header to fully process the scroll
   * restoration, then directly set the dataset attributes. By this point
   * Horizon's internal #lastScrollTop is already synced to the real
   * scroll position, so subsequent user scrolls behave correctly.
   */
  #forceHeaderVisible() {
    if (this.#savedScrollY <= 0) return;

    const header = document.querySelector('#header-component');
    if (!header) return;

    const sticky = header.getAttribute('sticky');
    if (!sticky) return;

    setTimeout(() => {
      if (sticky === 'scroll-up') {
        header.dataset.stickyState = 'active';
        header.dataset.scrollDirection = 'up';
      } else if (sticky === 'always') {
        header.dataset.scrollDirection = 'up';
      }
    }, 100);
  }

  #lockScroll() {
    this.#savedScrollY = window.scrollY;
    document.documentElement.style.setProperty(
      '--mini-search-scroll-top',
      `-${this.#savedScrollY}px`,
    );
    document.documentElement.classList.add('mini-search-open');
  }

  #unlockScroll() {
    document.documentElement.classList.remove('mini-search-open');
    document.documentElement.style.removeProperty('--mini-search-scroll-top');
    window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });
  }

  /**
   * Force the header out of transparent mode when mini-search is open.
   * Removes the transparent attribute so the Horizon header component
   * naturally reverts to its opaque state (correct colors, underlay visible).
   * @param {boolean} force
   */
  #overrideTransparentHeader(force) {
    const header = document.querySelector('#header-component');
    if (!header) return;

    if (force) {
      this.#savedTransparent = header.getAttribute('transparent');
      if (this.#savedTransparent !== null) {
        header.removeAttribute('transparent');
      }
      header.setAttribute('data-mini-search-open', '');
    } else {
      if (this.#savedTransparent !== null) {
        header.setAttribute('transparent', this.#savedTransparent);
        this.#savedTransparent = null;
      }
      header.removeAttribute('data-mini-search-open');
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) {
      this.close();
    }
  };
}

if (!customElements.get('mini-search')) {
  customElements.define('mini-search', MiniSearch);
}
